import { supabase } from '../supabase';
import { InsufficientStockError, UnauthorizedOperationError, createWarehouseError } from '../errors/warehouseErrors';

type SupabaseQueryBuilder = ReturnType<typeof supabase.from>;

export interface TransactionContext {
  organizationId: string;
  userId: string;
  ipAddress?: string;
  userAgent?: string;
}

/**
 * Execute multiple database operations within a transaction
 * @param operations Array of database operations to execute atomically
 * @returns Promise<T> Result of the transaction
 */
export async function withTransaction<T>(
  operations: (context: TransactionContext) => Promise<T>
): Promise<T> {
  // Get user context first
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new UnauthorizedOperationError('transaction', 'User not authenticated');

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('organization_id')
    .eq('id', userData.user.id)
    .single();

  if (!profile?.organization_id) {
    throw new UnauthorizedOperationError('transaction', 'User not associated with an organization');
  }

  const context: TransactionContext = {
    organizationId: profile.organization_id,
    userId: userData.user.id
  };

  // Execute operations within a transaction
  // Note: Supabase doesn't directly support transactions in client-side code
  // We'll implement a retry mechanism with optimistic locking
  const maxRetries = 3;
  let lastError: Error;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Execute operations
      const result = await operations(context);
      return result;
    } catch (error) {
      lastError = error as Error;
      
      // If it's a conflict error, retry with exponential backoff
      if (isRetryableError(error) && attempt < maxRetries) {
        const delay = Math.pow(2, attempt) * 100; // 200ms, 400ms, 800ms
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      throw error;
    }
  }
  
  throw lastError!;
}

interface DatabaseError {
  code?: string;
  message?: string;
}

/**
 * Check if an error is retryable (concurrent modification)
 */
function isRetryableError(error: unknown): boolean {
  const dbError = error as DatabaseError;
  // PostgreSQL conflict/serialization errors
  const retryableCodes = [
    '40001', // serialization_failure
    '40P01', // deadlock_detected
    '23505', // unique_violation (can happen during concurrent inserts)
  ];
  
  return retryableCodes.includes(dbError?.code) || 
         dbError?.message?.includes('conflict') ||
         dbError?.message?.includes('deadlock');
}

/**
 * Execute a batch of database operations with validation
 */
interface BatchOperation {
  table: string;
  operation: 'insert' | 'update' | 'delete';
  data?: Record<string, unknown>;
  conditions?: Record<string, unknown>;
  validation?: () => Promise<void>;
}

export async function executeBatch(operations: BatchOperation[]): Promise<void> {
  for (const op of operations) {
    // Run validation if provided
    if (op.validation) {
      await op.validation();
    }
    
    let query = supabase.from(op.table);
    
    switch (op.operation) {
      case 'insert':
        const { error: insertError } = await query.insert(op.data);
        if (insertError) throw insertError;
        break;
        
      case 'update':
        const { error: updateError } = await query
          .update(op.data)
          .match(op.conditions);
        if (updateError) throw updateError;
        break;
        
      case 'delete':
        const { error: deleteError } = await query
          .delete()
          .match(op.conditions);
        if (deleteError) throw deleteError;
        break;
    }
  }
}

/**
 * Bulk insert operation with batching and error handling
 */
export async function bulkInsert<T>(
  table: string,
  data: T[],
  options: {
    batchSize?: number;
    onConflict?: string;
    returning?: string;
    context?: TransactionContext;
  } = {}
): Promise<T[]> {
  const { batchSize = 100, onConflict, returning = '*' } = options;
  const results: T[] = [];
  
  // Process in batches to avoid query size limits
  for (let i = 0; i < data.length; i += batchSize) {
    const batch = data.slice(i, i + batchSize);
    
    try {
      let query = supabase.from(table).insert(batch);
      
      if (onConflict) {
        query = query.upsert(batch, { onConflict });
      }
      
      const { data: batchResults, error } = await query.select(returning);
      
      if (error) throw createWarehouseError(error, `bulk insert ${table}`);
      
      results.push(...(batchResults as T[]));
      
    } catch (error) {
      console.error(`Bulk insert failed for batch ${i / batchSize + 1}:`, error);
      throw error;
    }
  }
  
  return results;
}

/**
 * Bulk update operation using case-when pattern for performance
 */
export async function bulkUpdate<T extends Record<string, any>>(
  table: string,
  updates: Array<{ id: string; data: Partial<T> }>,
  options: {
    batchSize?: number;
    idColumn?: string;
    context?: TransactionContext;
  } = {}
): Promise<void> {
  const { batchSize = 100, idColumn = 'id' } = options;
  
  if (updates.length === 0) return;
  
  // Group updates by the fields being updated for better performance
  const fieldGroups = new Map<string, Array<{ id: string; data: Partial<T> }>>();
  
  for (const update of updates) {
    const fields = Object.keys(update.data).sort().join(',');
    if (!fieldGroups.has(fields)) {
      fieldGroups.set(fields, []);
    }
    fieldGroups.get(fields)!.push(update);
  }
  
  // Process each field group separately
  for (const [fields, groupUpdates] of fieldGroups) {
    for (let i = 0; i < groupUpdates.length; i += batchSize) {
      const batch = groupUpdates.slice(i, i + batchSize);
      
      try {
        // Use upsert for better performance with many updates
        const upsertData = batch.map(({ id, data }) => ({
          [idColumn]: id,
          ...data
        }));
        
        const { error } = await supabase
          .from(table)
          .upsert(upsertData, { 
            onConflict: idColumn,
            ignoreDuplicates: false 
          });
        
        if (error) throw createWarehouseError(error, `bulk update ${table}`);
        
      } catch (error) {
        console.error(`Bulk update failed for batch ${i / batchSize + 1}:`, error);
        throw error;
      }
    }
  }
}

/**
 * Bulk delete operation with batching
 */
export async function bulkDelete(
  table: string,
  ids: string[],
  options: {
    batchSize?: number;
    idColumn?: string;
    context?: TransactionContext;
  } = {}
): Promise<void> {
  const { batchSize = 100, idColumn = 'id' } = options;
  
  if (ids.length === 0) return;
  
  for (let i = 0; i < ids.length; i += batchSize) {
    const batch = ids.slice(i, i + batchSize);
    
    try {
      const { error } = await supabase
        .from(table)
        .delete()
        .in(idColumn, batch);
      
      if (error) throw createWarehouseError(error, `bulk delete ${table}`);
      
    } catch (error) {
      console.error(`Bulk delete failed for batch ${i / batchSize + 1}:`, error);
      throw error;
    }
  }
}

/**
 * Optimized query builder for common patterns
 */
export class QueryOptimizer {
  private static readonly DEFAULT_LIMIT = 50;
  private static readonly MAX_LIMIT = 1000;
  
  /**
   * Build paginated query with optimized ordering
   */
  static buildPaginatedQuery<T>(
    baseQuery: SupabaseQueryBuilder,
    options: {
      page?: number;
      limit?: number;
      orderBy?: string;
      orderDirection?: 'asc' | 'desc';
      searchColumns?: string[];
      searchTerm?: string;
    } = {}
  ) {
    const {
      page = 1,
      limit = this.DEFAULT_LIMIT,
      orderBy = 'created_at',
      orderDirection = 'desc',
      searchColumns = [],
      searchTerm
    } = options;
    
    const safeLimit = Math.min(limit, this.MAX_LIMIT);
    const offset = (page - 1) * safeLimit;
    
    let query = baseQuery;
    
    // Add search if provided
    if (searchTerm && searchColumns.length > 0) {
      const searchConditions = searchColumns
        .map(col => `${col}.ilike.%${searchTerm}%`)
        .join(',');
      query = query.or(searchConditions);
    }
    
    // Add ordering and pagination
    query = query
      .order(orderBy, { ascending: orderDirection === 'asc' })
      .range(offset, offset + safeLimit - 1);
    
    return query;
  }
  
  /**
   * Build query with selective field loading to reduce data transfer
   */
  static buildSelectiveQuery(
    table: string,
    fields: {
      basic: string[];
      relations?: Record<string, string[]>;
      computed?: string[];
    }
  ) {
    let selectClause = fields.basic.join(', ');
    
    // Add relations
    if (fields.relations) {
      for (const [relationName, relationFields] of Object.entries(fields.relations)) {
        selectClause += `, ${relationName}(${relationFields.join(', ')})`;
      }
    }
    
    // Add computed fields
    if (fields.computed) {
      selectClause += ', ' + fields.computed.join(', ');
    }
    
    return supabase.from(table).select(selectClause);
  }
}

/**
 * Validate stock quantities before operations
 */
export async function validateStockOperation(
  productId: string, 
  quantityChange: number, 
  context: TransactionContext
): Promise<void> {
  if (quantityChange >= 0) return; // No validation needed for positive changes
  
  // For negative changes, ensure enough stock in zones
  const { data: zoneStock } = await supabase
    .from('product_zone_storage')
    .select('quantity')
    .eq('product_id', productId)
    .eq('organization_id', context.organizationId);
    
  const totalZoneStock = zoneStock?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  
  if (Math.abs(quantityChange) > totalZoneStock) {
    throw new InsufficientStockError(totalZoneStock, Math.abs(quantityChange));
  }
}

/**
 * Optimistic locking helper - checks if a record hasn't been modified
 */
export async function checkOptimisticLock(
  table: string, 
  id: string, 
  expectedTimestamp: string
): Promise<boolean> {
  const { data } = await supabase
    .from(table)
    .select('updated_at')
    .eq('id', id)
    .single();
    
  return data?.updated_at === expectedTimestamp;
}